import { useScenarioForm } from './contexts';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from 'components/accordion/accordion';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
} from '@carbon/react';
import { Information, Draggable, CheckmarkOutline } from '@carbon/react/icons';
import { Toggle } from 'components/toggle/toggle';
import { ColorSwatch } from 'components/color-picker/color-picker';
import {
  FreeTextComboBox,
  FreeTextComboBoxItem,
} from 'components/free-text-combobox';
import sectionStyles from './config-sections.module.scss';
import styles from './formations.module.scss';
import reorderableListStyles from 'components/reorderable-list/reorderable-list.module.scss';
import {
  FormationMapping,
  FormationMappingResponse,
  FormationMappingsResponse,
  StructureGridCollectionItem,
} from './types';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useStore } from '@tanstack/react-form';
import { atom, useAtomValue } from 'jotai';
import { createResourceAtom } from 'lib/atoms';
import { FormationsSkeleton } from './section-skeletons';
import { INITIAL_AREA_CONFIG } from './default-values';
import { useConfigSwitch } from './hooks';

const FALLBACK_ATOM = atom(null);

const createAboveSentinel = (rank: number): FormationMapping => ({
  id: null,
  isSentinel: true,
  formationRank: rank,
  color: '#FFFFFF',
  positionalIndex: 0,
  geologicInterval: 'Above',
  structureGridSurface: null,
  structureGridHref: null,
  structureGridId: null,
  geologicIntervalId: null,
  validationStatus: null,
  typeCurveFormation: null,
});

const createBelowSentinel = (rank: number): FormationMapping => ({
  id: null,
  isSentinel: true,
  formationRank: rank,
  color: '#FFFFFF',
  positionalIndex: 99,
  geologicInterval: 'Below',
  structureGridSurface: null,
  structureGridHref: null,
  structureGridId: null,
  geologicIntervalId: null,
  validationStatus: null,
  typeCurveFormation: null,
});

const sortByPositionalIndex = (
  mappings: FormationMapping[]
): FormationMapping[] => {
  const top = mappings.filter((m) => m.positionalIndex === 0);
  const bottom = mappings.filter((m) => m.positionalIndex === 99);
  const middle = mappings.filter(
    (m) => m.positionalIndex !== 0 && m.positionalIndex !== 99
  );
  return [...top, ...middle, ...bottom].map((m, i) => ({
    ...m,
    formationRank: i + 1,
  }));
};

const isSentinelRow = (fm: FormationMapping) => fm.isSentinel;

const addSentinels = (mappings: FormationMapping[]): FormationMapping[] => [
  createAboveSentinel(1),
  ...mappings.map((m, i) => ({ ...m, formationRank: i + 2 })),
  createBelowSentinel(mappings.length + 2),
];

const mapResponseToFormationMapping = (
  resp: FormationMappingResponse
): FormationMapping => ({
  id: String(resp.id),
  isSentinel: resp.isSentinel,
  formationRank: resp.formationRank,
  color: resp.displayColor,
  positionalIndex: resp.geologicInterval?.positionalIndex ?? resp.formationRank,
  geologicInterval:
    !resp.geologicInterval?.name && resp.isSentinel
      ? resp.geologicInterval?.positionalIndex === 0
        ? 'Above'
        : 'Below'
      : (resp.geologicInterval?.name ?? ''),
  structureGridSurface: resp.gridSurface,
  structureGridHref: resp.structureGridId
    ? `/structure_grids/${resp.structureGridId}`
    : null,
  structureGridId: resp.structureGridId ?? null,
  geologicIntervalId: resp.geologicInterval?.id ?? null,
  validationStatus: null,
  typeCurveFormation: resp.typeCurveFormation,
});

const mapCollectionItemToFormationMapping = (
  item: StructureGridCollectionItem,
  index: number
): FormationMapping => ({
  id: null,
  isSentinel: false,
  formationRank: index + 1,
  color: item.geologicIntervalDisplayColor || '#cccccc',
  positionalIndex: item.geologicIntervalPositionalIndex,
  geologicInterval: item.geologicInterval,
  structureGridSurface: item.gridSurface,
  structureGridHref: item.href || null,
  structureGridId: item.structureGridId ?? null,
  geologicIntervalId: item.geologicIntervalId ?? null,
  validationStatus: null,
  typeCurveFormation: item.geologicInterval,
});

export const Formations = () => {
  const {
    form,
    selectedAreaConfig,
    setFieldBaseline,
    readOnly,
    structureGridCollectionData,
    manualFormationPriority: manualPriority,
    setManualFormationPriority: setManualPriority,
    formationRanks,
    setFormationRanks,
    formationsTrigger,
    setFormationMappingsReadyFor,
  } = useScenarioForm();
  const dragSourceRef = useRef<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  // Local state for rendering — avoids form.Subscribe issues with array fields
  const [localMappings, setLocalMappings] = useState<FormationMapping[]>([]);

  // Subscribe to form state changes so localMappings stays in sync when
  // save callbacks (e.g. saveFormationMappings.onSuccess) update form values
  // with server-assigned IDs after the initial POST.
  const formFormationMappings = useStore(
    form.store,
    (state) => state.values.formationMappings
  );

  const tcFormationOptions = useMemo((): FreeTextComboBoxItem[] => {
    // Geologic intervals are deduped case-insensitively (e.g. one "ONONDAGA").
    // Custom user-typed values are deduped case-sensitively so distinct casing
    // variants like "hi" and "HI" both appear, but values matching an existing
    // geologic interval (case-insensitive) are skipped.
    const geoKeys = new Set<string>();
    const items: FreeTextComboBoxItem[] = [];
    for (const fm of localMappings) {
      if (!fm.isSentinel && fm.geologicInterval) {
        const key = fm.geologicInterval.toLowerCase();
        if (!geoKeys.has(key)) {
          geoKeys.add(key);
          items.push({
            value: fm.geologicInterval,
            label: fm.geologicInterval,
          });
        }
      }
    }
    const customKeys = new Set<string>();
    for (const fm of localMappings) {
      if (fm.typeCurveFormation) {
        const exact = fm.typeCurveFormation;
        // Skip if it matches a geologic interval (case-insensitive)
        if (geoKeys.has(exact.toLowerCase())) continue;
        // Keep distinct casing variants
        if (!customKeys.has(exact)) {
          customKeys.add(exact);
          items.push({ value: exact, label: exact });
        }
      }
    }
    return items;
  }, [localMappings]);

  // Track default (original) formation names per row for the dropdown items
  const defaultFormationsRef = useRef<Map<string, string>>(new Map());

  // When manual priority is loaded from a saved config, formationRanks won't
  // have been initialized by the toggle handler.  Populate it from the loaded
  // formation data so drag-and-drop works and the rank column renders correctly.
  useEffect(() => {
    if (
      manualPriority &&
      formationRanks.length === 0 &&
      localMappings.length > 0
    ) {
      setFormationRanks(localMappings.map((m) => m.formationRank));
    }
  }, [manualPriority, formationRanks.length, localMappings, setFormationRanks]);

  // Merge in any IDs/server-side fields propagated through form state (e.g. after
  // the initial POST returns IDs via saveFormationMappings.onSuccess) so that the
  // next save can correctly classify rows as dirty (PATCH) instead of new (POST).
  useEffect(() => {
    if (!formFormationMappings || formFormationMappings.length === 0) return;
    setLocalMappings((prev) => {
      if (prev.length !== formFormationMappings.length) return prev;
      let changed = false;
      const next = prev.map((row, i) => {
        const formRow = formFormationMappings[i];
        if (formRow && formRow.id !== row.id) {
          changed = true;
          return { ...row, id: formRow.id };
        }
        return row;
      });
      return changed ? next : prev;
    });
  }, [formFormationMappings]);

  // Merge ids from form.state into the local row so updates don't accidentally
  // strip server-assigned IDs when localMappings is briefly out of sync (e.g.
  // between the post-save form.setFieldValue and the sync effect above).
  const mergeIdFromFormState = useCallback(
    (row: FormationMapping, index: number): FormationMapping => {
      const formRow = form.state.values.formationMappings?.[index];
      if (formRow && formRow.id && formRow.id !== row.id) {
        return { ...row, id: formRow.id };
      }
      return row;
    },
    [form]
  );

  const handleToggleManualPriority = useCallback(
    (isActive: boolean) => {
      setManualPriority(isActive);

      // Update algorithmicFormationPriority on the parent area config
      const currentAreaConfig = form.state.values.areaConfig;
      if (currentAreaConfig) {
        form.setFieldValue('areaConfig', {
          ...currentAreaConfig,
          algorithmicFormationPriority: !isActive,
        });
      }

      if (isActive) {
        const newRanks = Array.from(
          { length: localMappings.length },
          (_, i) => i + 1
        );
        setFormationRanks(newRanks);
      } else {
        setFormationRanks([]);
        // Revert formationRank to positional order
        const updated = localMappings.map((fm, i) => ({
          ...fm,
          formationRank: i + 1,
        }));
        setLocalMappings(updated);
        form.setFieldValue('formationMappings', updated);
      }
    },
    [
      localMappings,
      form,
      setFormationRanks,
      setManualPriority,
      setLocalMappings,
    ]
  );

  const handleDragStart = useCallback(
    (e: React.DragEvent, rowIndex: number) => {
      dragSourceRef.current = rowIndex;
      e.dataTransfer.effectAllowed = 'move';
    },
    []
  );

  const handleDragOver = useCallback((e: React.DragEvent, rowIndex: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(rowIndex);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOverIndex(null);
  }, []);

  const handleDrop = useCallback(
    (destRow: number) => {
      const srcRow = dragSourceRef.current;
      setDragOverIndex(null);
      dragSourceRef.current = null;
      if (srcRow === null || srcRow === destRow) return;

      const srcRank = formationRanks[srcRow];
      const destRank = formationRanks[destRow];

      const newRanks = formationRanks.map((rank) => {
        if (rank === srcRank) return destRank;
        if (srcRank < destRank) {
          if (rank > srcRank && rank <= destRank) return rank - 1;
        } else {
          if (rank >= destRank && rank < srcRank) return rank + 1;
        }
        return rank;
      });

      setFormationRanks(newRanks);

      // Sync formationRank on each mapping so dirty detection picks up the change
      const updated = localMappings.map((fm, i) => ({
        ...mergeIdFromFormState(fm, i),
        formationRank: newRanks[i],
      }));
      setLocalMappings(updated);
      form.setFieldValue('formationMappings', updated);
    },
    [
      formationRanks,
      localMappings,
      form,
      mergeIdFromFormState,
      setFormationRanks,
      setLocalMappings,
    ]
  );

  const handleDragEnd = useCallback(() => {
    dragSourceRef.current = null;
    setDragOverIndex(null);
  }, []);

  const isNewAreaConfig =
    !selectedAreaConfig?.id || selectedAreaConfig.id === 'local';

  const formationMappingsHref =
    selectedAreaConfig?.resources?.formationMappings?.href || null;

  const formationMappingsAtom = useMemo(() => {
    if (!formationMappingsHref) return FALLBACK_ATOM;
    return createResourceAtom<FormationMappingsResponse>(formationMappingsHref);
  }, [formationMappingsHref]);

  const formationMappingsData = useAtomValue(formationMappingsAtom);

  // Track whether API formation mappings have been loaded
  const apiFormationsLoadedRef = useRef(false);

  const initializedForConfigRef = useConfigSwitch(selectedAreaConfig, {
    // Save completed — sync localMappings from form state (server-assigned IDs).
    onSave: (configId) => {
      const formMappings = sortByPositionalIndex(
        form.state.values.formationMappings
      );
      setLocalMappings(formMappings);
      defaultFormationsRef.current.clear();
      formMappings.forEach((m, i) => {
        if (m.typeCurveFormation) {
          defaultFormationsRef.current.set(String(i), m.typeCurveFormation);
        }
      });
      apiFormationsLoadedRef.current = true;
      setFormationMappingsReadyFor(configId);
    },
    // Switch — reset to defaults
    onSwitch: () => {
      form.setFieldValue('formationMappings', []);
      setFieldBaseline('formationMappings', []);
      setLocalMappings([]);
      defaultFormationsRef.current.clear();
      apiFormationsLoadedRef.current = false;
      setFormationMappingsReadyFor(null);
    },
  });

  // When API data loads (saved area configs), override with fetched values.
  useEffect(() => {
    const configId = selectedAreaConfig?.id ?? null;
    if (initializedForConfigRef.current === configId) return;
    if (
      !formationMappingsData?.data ||
      !('results' in formationMappingsData.data)
    )
      return;
    initializedForConfigRef.current = configId;

    const results = formationMappingsData.data.results;
    const mapped = results.map(mapResponseToFormationMapping);
    // Keep BE order but ensure Above (0) is at top and Below (99) at bottom
    const top = mapped.filter((m) => m.positionalIndex === 0);
    const bottom = mapped.filter((m) => m.positionalIndex === 99);
    const middle = mapped.filter(
      (m) => m.positionalIndex !== 0 && m.positionalIndex !== 99
    );
    const mappings = [...top, ...middle, ...bottom];

    // Store default formation names keyed by array index
    defaultFormationsRef.current.clear();
    mappings.forEach((m, i) => {
      if (m.typeCurveFormation) {
        defaultFormationsRef.current.set(String(i), m.typeCurveFormation);
      }
    });

    setLocalMappings(mappings);
    form.setFieldValue('formationMappings', mappings);
    setFieldBaseline('formationMappings', mappings);
    apiFormationsLoadedRef.current = true;
    setFormationMappingsReadyFor(configId);
  }, [
    formationMappingsData,
    form,
    setFieldBaseline,
    selectedAreaConfig?.id,
    setFormationMappingsReadyFor,
    initializedForConfigRef,
  ]);

  // For new area configs OR when basin changes on existing configs,
  // populate formations from structure grid collection items.
  // When structureGridCollectionData is cleared (basin area changed),
  // either create an Above sentinel (if basin area has no structure grid
  // collection) or clear formations (structure grid collection will load).
  // formationsTrigger is incremented by area-configs.tsx when basin area
  // is selected, ensuring this effect re-runs even when
  // structureGridCollectionData stays null.
  useEffect(() => {
    // A new area config's table is built here, not loaded, so it is ready
    // right away. A saved config's table is ready once the API load above
    // finishes.
    if (isNewAreaConfig) setFormationMappingsReadyFor(INITIAL_AREA_CONFIG.id);

    if (!structureGridCollectionData) {
      // Read form's areaConfig synchronously to determine sentinel vs clear
      const areaConfig = form.state.values.areaConfig;
      const hasBasinAreaSelected = !!areaConfig?.areaGeometryId;
      const hasNoStructureGridCollection =
        !areaConfig?.structureGridCollectionId;

      if (
        hasBasinAreaSelected &&
        hasNoStructureGridCollection &&
        !apiFormationsLoadedRef.current
      ) {
        // Basin area selected with no structure grid collection → Above sentinel.
        // Set form value AND baseline so isFormModified stays false until the
        // user actually edits something. For new area configs the
        // handleSubmitForm `isNewArea` override force-POSTs the row on first
        // save anyway. For saved configs, Effect 344 will overwrite both with
        // real saved data once the API resolves.
        const sentinel = [createAboveSentinel(1)];
        setLocalMappings(sentinel);
        setFieldBaseline('formationMappings', sentinel);
        defaultFormationsRef.current.clear();
      } else if (!apiFormationsLoadedRef.current) {
        // No basin area yet, or waiting for a structure grid collection to load.
        // Do not clear when apiFormationsLoadedRef is true — after save,
        // isNewAreaConfig flips false and this effect re-runs; clearing only
        // the form value (not the baseline) left isFormModified stuck true.
        setLocalMappings([]);
        form.setFieldValue('formationMappings', []);
        defaultFormationsRef.current.clear();
        apiFormationsLoadedRef.current = false;
      }
      return;
    }
    const items = structureGridCollectionData.structureGridCollectionItems;
    if (!items || items.length === 0) return;

    // For new configs: always populate from collection
    // For saved configs: populate only if API mappings haven't loaded yet
    //   (basin changed scenario — the flag was reset above when data was cleared)
    if (isNewAreaConfig || !apiFormationsLoadedRef.current) {
      const mappings = items.map(mapCollectionItemToFormationMapping);
      const withSentinels = sortByPositionalIndex(addSentinels(mappings));

      // Store default formation names keyed by array index (including sentinels)
      defaultFormationsRef.current.clear();
      withSentinels.forEach((m, i) => {
        if (m.typeCurveFormation) {
          defaultFormationsRef.current.set(String(i), m.typeCurveFormation);
        }
      });
      setLocalMappings(withSentinels);
      // Set form value AND baseline so isFormModified stays false until the
      // user actually edits something. For new area configs the
      // handleSubmitForm `isNewArea` override force-POSTs the rows on first
      // save anyway. For saved configs (race: this effect ran before the
      // formation-mappings API call finished), Effect 344 above will overwrite
      // both form value and baseline with the real saved mappings once the
      // API resolves.
      setFieldBaseline('formationMappings', withSentinels);
    }
  }, [
    structureGridCollectionData,
    formationsTrigger,
    isNewAreaConfig,
    form,
    setFieldBaseline,
    setFormationMappingsReadyFor,
  ]);

  const handleColorChange = useCallback(
    (index: number, color: string) => {
      const updated = localMappings.map((fm, i) => {
        const merged = mergeIdFromFormState(fm, i);
        return i === index ? { ...merged, color } : merged;
      });
      setLocalMappings(updated);
      form.setFieldValue('formationMappings', updated);
    },
    [form, localMappings, mergeIdFromFormState]
  );

  const handleFormationChange = useCallback(
    (index: number, name: string | null) => {
      const updated = localMappings.map((fm, i) => {
        const merged = mergeIdFromFormState(fm, i);
        return i === index ? { ...merged, typeCurveFormation: name } : merged;
      });
      setLocalMappings(updated);
      form.setFieldValue('formationMappings', updated);
    },
    [form, localMappings, mergeIdFromFormState]
  );

  const hasFormations = localMappings.length > 0;
  const hasBasinArea = !!selectedAreaConfig?.areaGeometry?.geoJson;

  return (
    <Accordion defaultExpanded>
      <AccordionSummary className={sectionStyles.accordionSummaryBorder}>
        <h3 className={sectionStyles.sectionTitle}>Formations</h3>
      </AccordionSummary>
      <AccordionDetails>
        <div className={sectionStyles.sectionDetails}>
          <p className={styles.description}>
            Define and map Type Curve Formations to Geo Intervals.
          </p>

          {hasBasinArea && (
            <div className={styles.toggleRow}>
              <Toggle
                isActive={manualPriority}
                handleToggle={handleToggleManualPriority}
                label={
                  manualPriority
                    ? 'Manual Formation Priority'
                    : 'Algorithmic Formation Priority'
                }
                size="small"
                disabled={readOnly}
              />
            </div>
          )}

          {formationMappingsData?.loading ? (
            <FormationsSkeleton />
          ) : !hasFormations ? (
            <p className={styles.emptyMessage}>
              Select a Basin Area to view formations.
            </p>
          ) : (
            <div className={styles.tableContainer}>
              <TableContainer>
                <Table size="md">
                  <TableHead className={styles.stickyHead}>
                    <TableRow>
                      <TableHeader>Color</TableHeader>
                      <TableHeader>Positional Index</TableHeader>
                      <TableHeader>Geologic Interval</TableHeader>
                      <TableHeader>Structure Grid Surface</TableHeader>
                      <TableHeader>Validation Status</TableHeader>
                      <TableHeader>Type Curve Formation</TableHeader>
                      {manualPriority && (
                        <TableHeader>Formation Rank</TableHeader>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {localMappings.map((fm, index) => {
                      const sentinel = isSentinelRow(fm);

                      return (
                        <TableRow key={fm.id ?? String(fm.formationRank)}>
                          <TableCell>
                            <div className={styles.colorCell}>
                              <ColorSwatch
                                color={fm.color || '#cccccc'}
                                onChange={(color) =>
                                  handleColorChange(index, color)
                                }
                                popoverPosition="top"
                              />
                              <span className={styles.colorHex}>
                                {(fm.color || '#cccccc').toUpperCase()}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>{fm.positionalIndex}</TableCell>
                          <TableCell>
                            {sentinel ? (
                              <span className={styles.sentinelLabel}>
                                {fm.geologicInterval}
                                <Tooltip
                                  align="right"
                                  label={
                                    fm.geologicInterval === 'Above'
                                      ? 'Wells above defined Structure Grid Surfaces will be assigned corresponding Type Curve Formation.'
                                      : 'Wells below defined Structure Grid Surfaces will be assigned corresponding Type Curve Formation.'
                                  }
                                >
                                  <Information size={14} />
                                </Tooltip>
                              </span>
                            ) : (
                              fm.geologicInterval
                            )}
                          </TableCell>
                          <TableCell>
                            {fm.structureGridSurface ? (
                              <a
                                href={fm.structureGridHref ?? '#'}
                                className={styles.gridSurfaceLink}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {fm.structureGridSurface}
                              </a>
                            ) : (
                              '—'
                            )}
                          </TableCell>
                          <TableCell>
                            {!sentinel && fm.structureGridSurface ? (
                              <span className={styles.validStatus}>
                                <CheckmarkOutline
                                  size={16}
                                  className={styles.validIcon}
                                />
                                Valid
                              </span>
                            ) : null}
                          </TableCell>
                          <TableCell>
                            <FreeTextComboBox
                              id={`tc-formation-${fm.id ?? fm.formationRank}`}
                              // className={styles.uppercaseInput}
                              items={tcFormationOptions}
                              selectedItem={
                                fm.typeCurveFormation
                                  ? {
                                      value: fm.typeCurveFormation,
                                      label: fm.typeCurveFormation,
                                    }
                                  : null
                              }
                              itemToString={(item) => item?.label ?? ''}
                              onChange={({ selectedItem }) => {
                                handleFormationChange(
                                  index,
                                  (selectedItem as FreeTextComboBoxItem | null)
                                    ?.value ?? null
                                );
                              }}
                              placeholder="Type or select formation..."
                              disabled={readOnly}
                            />
                          </TableCell>
                          {manualPriority && (
                            <TableCell>
                              <div className={styles.rankDragWrapper}>
                                <div
                                  className={`${reorderableListStyles.item} ${
                                    styles.rankDragItem
                                  }${
                                    dragOverIndex === index
                                      ? ` ${styles.rankDragItemDragOver}`
                                      : ''
                                  }${
                                    readOnly
                                      ? ` ${reorderableListStyles.disabled}`
                                      : ''
                                  }`}
                                  draggable={!readOnly}
                                  onDragStart={(e) =>
                                    !readOnly && handleDragStart(e, index)
                                  }
                                  onDragOver={(e) =>
                                    !readOnly && handleDragOver(e, index)
                                  }
                                  onDragLeave={handleDragLeave}
                                  onDrop={() => !readOnly && handleDrop(index)}
                                  onDragEnd={handleDragEnd}
                                >
                                  <span className={reorderableListStyles.grip}>
                                    <Draggable />
                                  </span>
                                  <span className={reorderableListStyles.rank}>
                                    {formationRanks[index] ?? index + 1}
                                  </span>
                                </div>
                              </div>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                    {localMappings.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={manualPriority ? 7 : 6}>
                          No formation mappings found.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
          )}
        </div>
      </AccordionDetails>
    </Accordion>
  );
};
